import { rtkApi as api } from '../rtkApi';

export type ContractDocumentStatus = 'pending_review' | 'approved' | 'rejected';
export type ContractDocumentDecision = 'approved' | 'rejected' | 'acknowledged';

export interface ContractDocumentReview {
  decision: ContractDocumentDecision;
  reviewerRole: 'buyer' | 'seller' | 'agent' | 'admin';
  reviewerName?: string;
  comment?: string;
  createdAt: string;
}

export interface ContractDocument {
  _id: string;
  contractId: string;
  type: string;
  category: string;
  title: string;
  required: boolean;
  requiredForStage?: string;
  file: {
    storage: 'local';
    storageKey: string;
    originalName: string;
    filename: string;
    mimeType: string;
    size: number;
  };
  version: number;
  supersedesDocumentId?: string;
  uploadedByRole: 'buyer' | 'seller' | 'agent' | 'admin';
  uploadedByName?: string;
  uploadedAt: string;
  status: ContractDocumentStatus;
  reviews?: ContractDocumentReview[];
  downloadCount?: number;
  downloadedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ListContractDocumentsResponse {
  success: boolean;
  data: ContractDocument[];
  count: number;
}

export interface ReviewDocumentRequest {
  documentId: string;
  decision: ContractDocumentDecision;
  comment?: string;
}

export const documentsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getContractDocuments: builder.query<ListContractDocumentsResponse, string>({
      query: (contractId) => ({
        url: `/documents/contract/${contractId}`,
        method: 'GET',
      }),
      providesTags: ['ContractDocuments'],
    }),

    uploadContractDocument: builder.mutation<
      { success: boolean; data: ContractDocument; message?: string },
      {
        contractId: string;
        file: File;
        type: string;
        category?: string;
        title?: string;
      }
    >({
      query: ({ contractId, file, type, category, title }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', type);
        if (category) formData.append('category', category);
        if (title) formData.append('title', title);

        return {
          url: `/documents/contract/${contractId}/upload`,
          method: 'POST',
          data: formData,
          headers: {
            // Override axiosBaseQuery default JSON header
            'Content-Type': 'multipart/form-data',
          },
        };
      },
      invalidatesTags: (_result, _err, { contractId }) => ['ContractDocuments'],
    }),

    reviewContractDocument: builder.mutation<
      { success: boolean; data: ContractDocument; message?: string },
      ReviewDocumentRequest
    >({
      query: ({ documentId, decision, comment }) => ({
        url: `/documents/${documentId}/review`,
        method: 'POST',
        data: { decision, comment },
      }),
      invalidatesTags: ['ContractDocuments'],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetContractDocumentsQuery,
  useUploadContractDocumentMutation,
  useReviewContractDocumentMutation,
} = documentsApi;
